import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputNumber, Select, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import { NdoeActionButton } from '../../components/ndoe';
import { messageErreur } from '../../components/ndoe/chargement';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Chantier #3 Lot 2 — éditeur dédié du « recul d'antidatage » d'une date de
// livraison : défaut global + surcharge par rôle + exceptions par utilisateur.
// L'admin est TOUJOURS illimité (borne codée en dur côté serveur). Écrit les 3
// clés du schéma (livraison_recul_defaut_jours / _roles / _users) via l'API parametre.
const ROLES_CONFIGURABLES = [
  { role: 'livreur', label: 'Livreur' },
  { role: 'secretaire', label: 'Secrétaire' },
  { role: 'vendeur', label: 'Vendeur' },
];

const parseList = (raw) => {
  if (!raw) return [];
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch (e) { return []; }
};

const ReculLivraisonSettings = ({ items, onSaved }) => {
  const valeurDe = (cle) => (items.find((s) => s.cle === cle) || {}).valeur;

  const [defaut, setDefaut] = useState(3);
  const [roles, setRoles] = useState({});          // { role: jours|'' }
  const [users, setUsers] = useState([]);          // [{ id, jours }]
  const [userList, setUserList] = useState([]);
  const [erreurUsers, setErreurUsers] = useState(null);
  const [saving, setSaving] = useState({});

  // Initialise l'état depuis les valeurs serveur UNE SEULE FOIS (au 1er chargement
  // des items). Sans ce garde, chaque enregistrement (qui remplace la référence de
  // `items` via onSaved) réinitialiserait les 3 sections et écraserait les saisies
  // en cours des autres sections.
  const inited = useRef(false);
  useEffect(() => {
    if (inited.current || !items || !items.length) return;
    inited.current = true;
    const d = valeurDe('livraison_recul_defaut_jours');
    setDefaut(d != null && d !== '' ? Number(d) : 3);
    const r = {};
    parseList(valeurDe('livraison_recul_roles')).forEach((x) => { if (x && x.role != null) r[x.role] = Number(x.jours); });
    setRoles(r);
    setUsers(parseList(valeurDe('livraison_recul_users')).map((x) => ({ id: Number(x.id), jours: Number(x.jours) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    axios.get(`${DOMAIN}/api/user/getUser`)
      .then(({ data }) => setUserList(Array.isArray(data) ? data : []))
      // Sans la liste, cet écran de réglage ne sert à rien : on dit pourquoi.
      .catch((e) => { setUserList([]); setErreurUsers(messageErreur(e, 'la liste des utilisateurs')); });
  }, []);

  const put = async (cle, valeur, libelle) => {
    if (saving[cle]) return;
    setSaving((m) => ({ ...m, [cle]: true }));
    try {
      const { data } = await axios.put(`${DOMAIN}/api/parametre/settings/${cle}`, { valeur });
      onSaved?.(cle, data.valeur);
      toast.success(`${libelle} enregistré.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setSaving((m) => ({ ...m, [cle]: false })); }
  };

  const saveDefaut = () => put('livraison_recul_defaut_jours', String(defaut ?? 3), 'Recul par défaut');
  const saveRoles = () => {
    const arr = Object.entries(roles)
      .filter(([, j]) => j !== '' && j != null && Number.isFinite(Number(j)))
      .map(([role, j]) => ({ role, jours: Number(j) }));
    put('livraison_recul_roles', JSON.stringify(arr), 'Recul par rôle');
  };
  const saveUsers = () => {
    const arr = users
      .filter((u) => u.id && u.jours !== '' && u.jours != null && Number.isFinite(Number(u.jours)))
      .map((u) => ({ id: Number(u.id), jours: Number(u.jours) }));
    put('livraison_recul_users', JSON.stringify(arr), 'Exceptions par utilisateur');
  };

  const userName = (id) => {
    const u = userList.find((x) => Number(x.id) === Number(id));
    return u ? (u.username || `#${id}`) : `#${id}`;
  };
  // Seuls les LIVREURS confirment des livraisons (garde requireDeliveryAccess),
  // donc seules leurs exceptions ont un effet réel.
  const usersDispo = useMemo(
    () => userList.filter((u) => u.role === 'livreur' && !users.some((x) => Number(x.id) === Number(u.id))),
    [userList, users],
  );

  return (
    <div className="pb-group ndoe-card">
      <p style={{ marginTop: 0, color: 'var(--ndoe-muted)' }}>
        Jusqu’à quelle ancienneté un livreur peut saisir une date de livraison passée (livraison enregistrée en retard).
        La priorité est : <b>exception utilisateur</b> → <b>rôle</b> → <b>défaut</b>. L’<b>admin n’a jamais de limite</b>.
        Le recul s’applique à la <b>confirmation de livraison</b> ; aujourd’hui seuls les <b>livreurs</b> y sont soumis.
      </p>

      {/* Défaut global */}
      <div className="pb-row">
        <div className="pb-lab"><b>Recul maximal par défaut</b><small>S’applique à tout rôle sans réglage spécifique.</small></div>
        <div className="pb-ctl">
          <InputNumber inputMode="decimal" min={0} max={3650} value={defaut} onChange={(n) => setDefaut(n == null ? 0 : n)} addonAfter="jours" />
          <NdoeActionButton size="sm" variant="primary" loading={!!saving.livraison_recul_defaut_jours} onClick={saveDefaut}>Enregistrer</NdoeActionButton>
        </div>
      </div>

      {/* Par rôle */}
      <div className="pb-row">
        <div className="pb-lab"><b>Recul par rôle</b><small>Laisser vide = utilise le défaut. Admin = illimité (non listé).</small></div>
        <div className="pb-ctl" style={{ flexWrap: 'wrap', gap: 10 }}>
          {ROLES_CONFIGURABLES.map(({ role, label }) => (
            <span key={role} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--ndoe-ink)' }}>{label}</span>
              <InputNumber inputMode="decimal"
                min={0} max={3650} style={{ width: 110 }} placeholder="défaut"
                value={roles[role] ?? null}
                onChange={(n) => setRoles((m) => ({ ...m, [role]: n == null ? '' : n }))}
                addonAfter="j"
              />
            </span>
          ))}
          <NdoeActionButton size="sm" variant="primary" loading={!!saving.livraison_recul_roles} onClick={saveRoles}>Enregistrer</NdoeActionButton>
        </div>
      </div>

      {/* Exceptions par utilisateur */}
      <div className="pb-row">
        <div className="pb-lab"><b>Exceptions par utilisateur</b><small>Surcharge tout pour une personne précise.</small></div>
        <div className="pb-ctl" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          {users.map((u, i) => (
            <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Tag color="gold">{userName(u.id)}</Tag>
              <InputNumber inputMode="decimal"
                min={0} max={3650} style={{ width: 120 }}
                value={u.jours} addonAfter="jours"
                onChange={(n) => setUsers((arr) => arr.map((x, j) => (j === i ? { ...x, jours: n == null ? '' : n } : x)))}
              />
              <NdoeActionButton size="sm" variant="ghost" iconOnly aria-label={`Retirer ${userName(u.id)}`} icon={<DeleteOutlined />} onClick={() => setUsers((arr) => arr.filter((_, j) => j !== i))} />
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Select
              showSearch style={{ minWidth: 240 }} placeholder="Ajouter un utilisateur…" value={null}
              optionFilterProp="label"
              options={usersDispo.map((u) => ({ value: u.id, label: `${u.username || ('#' + u.id)}${u.role ? ' · ' + u.role : ''}` }))}
              onChange={(id) => { if (id != null) setUsers((arr) => [...arr, { id, jours: defaut }]); }}
              // Une liste vide par panne ressemblait à « plus personne à ajouter ».
              notFoundContent={erreurUsers || undefined}
            />
          </span>
          {/* HORS du <span inline-flex> : dedans, le message s'alignait à côté
              du menu au lieu de passer dessous (et un <div> dans un <span> est
              un imbriquement invalide). */}
          {erreurUsers && (
            <div style={{ color: '#b42318', fontSize: 12 }}>
              {erreurUsers} Les exceptions déjà enregistrées restent en place.
            </div>
          )}
          <div>
            <NdoeActionButton size="sm" variant="primary" loading={!!saving.livraison_recul_users} onClick={saveUsers}>Enregistrer les exceptions</NdoeActionButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReculLivraisonSettings;
